// MARK://dhk8/pos/lib/branding-loader
// Runtime branding. The shell ships with a build-time default brand
// (import.meta.env.VITE_BRAND, injected by @dockethub/vite-plugin-brand), then the
// backend's branding record (from /v1/me/branding, served by dhk8-platform-tenants)
// can refine it per-tenant: display name, logo URL, and the POS accent color.
// Nothing about identity is hardcoded in components — they read getBranding().
//
// The accent color is stored backend-side as a single CSS color; the client
// expands it into the full `--pos-*` token map at load time via the accent-ramp
// engine (lib/accent-ramp.ts), so no rebuild is needed when a tenant changes it.
//
// NOTE: this loader is POS-only. The admin shells keep the NintaiGO green and do
// NOT call loadBranding() for the accent — see the brief.
//
// ADR-0019/0032. Backend chain: dhk8-platform-tenants /v1/me/branding.

import { apiBase } from './api-origin.js'
import { apiFetch, ApiError } from './api.js'
import { buildAccentRamp, applyAccentRamp, ACCENT_TOKEN_KEYS } from './accent-ramp.js'

// `dhk8-platform-tenants` sits behind the ingress at /v1/dhk8-platform-tenants/…
// (path-prefix route `/v1/dhk8-platform-tenants(/|$)(.*)`). Its own routes are
// versioned, hence the doubled `/v1`.
const BRANDING_PATH = '/dhk8-platform-tenants/v1/me/branding'
const brandingUrl = (): string => `${apiBase()}${BRANDING_PATH}`

export interface Branding {
  /** Commercial brand id (matches data-brand / theme-<id>.css). */
  brand: string
  /** Display name shown in headers / titles. */
  name: string
  /** Tenant logo URL, or null to use the brand default mark. */
  logoUrl: string | null
  /** Per-tenant accent color (any CSS color), or null to keep the brand default. */
  accentColor: string | null
  /** Resolved accent-ramp token map currently applied (derived from accentColor). */
  tint?: Record<string, string>
}

const BUILD_BRAND = (import.meta.env.VITE_BRAND as string | undefined) ?? 'nintaigo'
const BUILD_BRAND_NAME = (import.meta.env.VITE_BRAND_NAME as string | undefined) ?? 'NintaiGO'

let current: Branding = {
  brand: BUILD_BRAND,
  name: BUILD_BRAND_NAME,
  logoUrl: null,
  accentColor: null,
}

interface BackendBrandingDto {
  brand?: string | null
  name?: string | null
  displayName?: string | null
  logoUrl?: string | null
  accentColor?: string | null
  // Legacy / forward-compat: an explicit token map wins over accentColor.
  tint?: Record<string, string> | null
}

function applyBranding(b: Branding): void {
  current = b
  if (typeof document !== 'undefined') {
    const root = document.documentElement
    if (b.brand) root.dataset.brand = b.brand
    if (b.tint) applyAccentRamp(b.tint, root)
  }
}

/** Last known branding. Synchronous; safe to call from render. */
export function getBranding(): Branding {
  return current
}

/**
 * Apply a new accent color immediately to `:root` and remember it. Returns the
 * resolved token map. Used by the Settings color picker for live-apply (and by
 * loadBranding on boot). Pass null/'' to clear back to the build-time default.
 */
export function applyAccentColor(accentColor: string | null | undefined): Record<string, string> {
  const accent = accentColor && accentColor.trim() ? accentColor.trim() : null
  const ramp = buildAccentRamp(accent)
  applyBranding({ ...current, accentColor: accent, tint: ramp })
  return ramp
}

/** Remove every accent token override from `:root` (back to tokens.css defaults). */
export function clearAccentOverride(): void {
  if (typeof document !== 'undefined') {
    const root = document.documentElement
    for (const k of ACCENT_TOKEN_KEYS) root.style.removeProperty(k)
  }
  const { tint: _tint, ...rest } = current
  current = { ...rest, accentColor: null }
}

/**
 * Fetch and apply the tenant branding record from dhk8-platform-tenants.
 * Best-effort: on any failure the build-time brand stays in effect.
 * Call once after auth (POS only).
 */
export async function loadBranding(signal?: AbortSignal): Promise<Branding> {
  try {
    const dto = await apiFetch<BackendBrandingDto | null>(brandingUrl(), { signal })
    if (!dto) return current
    const accentColor = dto.accentColor && dto.accentColor.trim() ? dto.accentColor.trim() : null
    const tint = dto.tint && Object.keys(dto.tint).length > 0 ? dto.tint : accentColor ? buildAccentRamp(accentColor) : undefined
    applyBranding({
      brand: typeof dto.brand === 'string' && dto.brand ? dto.brand : current.brand,
      name: dto.displayName ?? dto.name ?? current.name,
      logoUrl: dto.logoUrl ?? null,
      accentColor,
      ...(tint ? { tint } : {}),
    })
  } catch (e) {
    // 404 / network / abort → keep build-time defaults.
    if (e instanceof ApiError) {
      // swallow — branding is best-effort
    }
  }
  return current
}

/**
 * Persist a new accent color for the current tenant and live-apply it.
 * Throws ApiError on failure (caller surfaces a toast). Returns the resolved
 * token map. Pass null to reset to the brand default.
 */
export async function saveAccentColor(accentColor: string | null): Promise<Record<string, string>> {
  await apiFetch<unknown>(brandingUrl(), { method: 'PUT', body: { accentColor } })
  if (accentColor && accentColor.trim()) return applyAccentColor(accentColor)
  clearAccentOverride()
  return {}
}
